import { useMemo } from "react";
import {
  MenuItem,
  MenuSeparator,
  DataTable,
  DataTableEmpty,
  createColumnHelper,
  Badge,
  Box,
  Text,
  Icon,
  type DataTableColumnDef,
  type DataTableGroupType,
  type OnChangeFn,
  type SortingState,
} from "@salestrak/ui";

import { EntityTag } from "#app/components/entity";
import { type WorkflowRow, WorkflowsListDataDocument } from "#app/lib/data";
import { usePermission } from "#app/lib/hooks/use-permission";
import type { TableViewTableProps } from "#app/lib/hooks/use-table-view-settings";
import {
  GroupQueryProvider,
  useGroupStates,
} from "#app/lib/hooks/group-query-provider";
import type { Omittable } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type WorkflowsTableProps = Omittable<{
  where: Record<string, unknown>;
  // Undefined when the list defers ordering to the API (`deferOrderBy`) and the
  // user has not sorted — the variable is then simply omitted.
  orderBy: Record<string, string> | undefined;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  searchValue: string;
  /**
   * Column definitions from `useWorkflowColumns` — built by the parent page
   * so the `DataTableColumnSettings` panel in its header shares the same defs.
   */
  columns: DataTableColumnDef<WorkflowRow>[];
  /** Per-user order/hidden/width customization from `useTableViewSettings`. */
  tableViewProps?: TableViewTableProps;
  onEdit: (workflowId: string) => void;
  onSetDefault: (workflow: WorkflowRow) => void;
  onRequestDelete: (workflow: WorkflowRow) => void;
}>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const h = createColumnHelper<WorkflowRow>();
const getRowId = (r: WorkflowRow) => r.workflowId;

// ---------------------------------------------------------------------------
// Per-group query helpers
// ---------------------------------------------------------------------------

const getConnection = (data: any) => data?.workflows;

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

export function useWorkflowColumns(
  onEdit: (workflowId: string) => void,
): DataTableColumnDef<WorkflowRow>[] {
  return useMemo<DataTableColumnDef<WorkflowRow>[]>(
    () => [
      h.hoverAction({
        accessorKey: "name",
        header: "Name",
        enableSorting: true,
        content: (workflow) => (
          <Box direction="row" align="center" gap="xs">
            <EntityTag
              entity={{
                kind: "workflow",
                id: workflow.workflowId,
                workflow: { name: workflow.name, icon: workflow.icon },
              }}
              interactive={false}
            />
            {workflow.key ? (
              <>
                <Text size="xs" variant="muted">{"·"}</Text>
                <Text size="xs" variant="muted" truncate={1}>
                  {workflow.key}
                </Text>
              </>
            ) : null}
            {workflow.isDefault ? (
              <Badge variant="secondary" size="sm">
                Default
              </Badge>
            ) : null}
          </Box>
        ),
        onClickAction: (workflow) => onEdit(workflow.workflowId),
        actionLabel: () => "Edit workflow…",
      }),

      h.date({
        accessorKey: "lastUpdateDate",
        header: "Updated",
        enableSorting: true,
      }),
    ],
    [onEdit],
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function WorkflowsTable({
  where: whereProp,
  orderBy: orderByProp,
  sorting,
  onSortingChange,
  searchValue,
  columns,
  tableViewProps,
  onEdit,
  onSetDefault,
  onRequestDelete,
}: WorkflowsTableProps) {
  const canDeleteWorkflows = usePermission("delete.workflows");

  // --- Group query state ---
  const { states, registerState, getGroupProps } = useGroupStates<WorkflowRow>();

  const groups = useMemo<DataTableGroupType<WorkflowRow>[]>(
    () => [
      {
        key: "__all__",
        label: "All workflows",
        totalCount: states.get("__all__")?.total,
        ...getGroupProps("__all__"),
      },
    ],
    [states, getGroupProps],
  );

  return (
    <>
      {/* Ungrouped query provider */}
      <GroupQueryProvider<WorkflowRow>
        groupKey="__all__"
        document={WorkflowsListDataDocument}
        variables={{ where: whereProp, orderBy: orderByProp, limit: 50 }}
        getConnection={getConnection}
        onStateChange={registerState}
      />

      <Box direction="column" paddingTop="xs" paddingBottom="lg">
        <DataTable
          columns={columns}
          groups={groups}
          getRowId={getRowId}
          sorting={sorting}
          onSortingChange={onSortingChange}
          stickyHeader
          density="regular"
          fillColumnId="name"
          paddingX="lg"
          {...tableViewProps}
          renderRowActions={(workflow) => (
            <>
              <MenuItem onClick={() => onEdit(workflow.workflowId)}>
                <Icon name="pencil" size="lg" color="muted" />
                <Text size="sm">Edit</Text>
              </MenuItem>
              <MenuItem
                onClick={() => onSetDefault(workflow)}
                disabled={workflow.isDefault}
              >
                <Icon name="star" size="lg" color="muted" />
                <Text size="sm">Set as default</Text>
              </MenuItem>
              <MenuSeparator />
              <MenuItem
                onClick={() => onRequestDelete(workflow)}
                disabled={workflow.isDefault || !canDeleteWorkflows}
              >
                <Icon name="trash-2" size="lg" color="destructive" />
                <Text size="sm" variant="destructive">Delete</Text>
              </MenuItem>
            </>
          )}
          emptyState={
            <DataTableEmpty
              message={
                searchValue
                  ? "No workflows match your search"
                  : "No workflows found"
              }
            />
          }
        />
      </Box>
    </>
  );
}
